import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as Screenshot } from "../../assets/icons/svg/screenshot.svg";
import { ReactComponent as Video } from "../../assets/icons/svg/video.svg";
import { DEFAULT_FONT_FAMILY } from "../../config/fontConfig";

export interface ToolItem {
  id: string;
  icon: React.ReactNode;
  name: string;
}

interface ToolBoxProps {
  onToolClick: (toolId: string) => void;
  isHidden?: boolean;
  hasUrl?: boolean;
  isFullscreen?: boolean;
}

const Toast: React.FC<{ message: string; onClose: () => void }> = ({
  message,
  onClose,
}) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-4 right-4 z-50 max-w-md">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-red-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <p
              className="text-sm font-medium text-red-800"
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              URL Required
            </p>
            <p
              className="text-sm text-red-700 mt-1"
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              {message}
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 flex-shrink-0 bg-red-50 rounded-md inline-flex text-red-400 hover:text-red-500 focus:outline-none"
          >
            <span
              className="sr-only"
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              Close
            </span>
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const ToolBox: React.FC<ToolBoxProps> = ({
  onToolClick,
  isHidden = false,
  hasUrl = false,
  isFullscreen = false,
}) => {
  const [hoveredTool, setHoveredTool] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isIframeFocused, setIsIframeFocused] = useState(false);

  const tools: ToolItem[] = [
    {
      id: "screenshot",
      icon: (
        <Screenshot className="w-5 h-5 xl:w-7 xl:h-7 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10" />
      ),
      name: isIframeFocused ? "Screenshot" : "Screenshot (S)",
    },
    {
      id: "record",
      icon: (
        <Video className="w-5 h-5 xl:w-7 xl:h-7 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10" />
      ),
      name: isIframeFocused ? "Screenrecord" : "Screenrecord (R)",
    },
  ];

  const handleToolClick = (toolId: string) => {
    if (!hasUrl) {
      setToastMessage("Please load a website URL first before using this tool");
      setShowToast(true);
      return;
    }
    onToolClick(toolId);
  };
  useEffect(() => {
    const handlePreviewOpened = () => {
      console.log("ToolBox: Preview opened - disabling shortcuts");
      setIsPreviewOpen(true);
    };

    const handlePreviewClosed = () => {
      console.log("ToolBox: Preview closed - enabling shortcuts");
      setIsPreviewOpen(false);
    };

    window.addEventListener("preview-opened", handlePreviewOpened);
    window.addEventListener("preview-closed", handlePreviewClosed);

    return () => {
      window.removeEventListener("preview-opened", handlePreviewOpened);
      window.removeEventListener("preview-closed", handlePreviewClosed);
    };
  }, []);

  // Track when the iframe has focus so shortcuts pause and the user can type freely
  useEffect(() => {
    const handleWindowBlur = () => {
      if (document.activeElement?.tagName === "IFRAME") {
        setIsIframeFocused(true);
      }
    };
    const handleWindowFocus = () => {
      setIsIframeFocused(false);
    };
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("focus", handleWindowFocus);
    return () => {
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip shortcuts when iframe is focused so the user can type inside it
      if (isPreviewOpen || isIframeFocused) return;

      const target = event.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        return;
      }

      if (event.key.toLowerCase() === "s") {
        event.preventDefault();
        if (!hasUrl) {
          setToastMessage(
            "Please load a website URL first before taking a screenshot",
          );
          setShowToast(true);
          return;
        }
        onToolClick("screenshot");
      } else if (event.key.toLowerCase() === "r") {
        event.preventDefault();
        if (!hasUrl) {
          setToastMessage("Please load a website URL first before recording");
          setShowToast(true);
          return;
        }
        onToolClick("record");
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onToolClick, hasUrl, isPreviewOpen, isIframeFocused]);

  return (
    <>
      {showToast && (
        <Toast message={toastMessage} onClose={() => setShowToast(false)} />
      )}

      <div className="relative flex flex-col items-center">
        {!isFullscreen && hoveredTool && (
          <div
            className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded-[4px] transition-opacity duration-300 whitespace-nowrap pointer-events-none"
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            {hoveredTool}
          </div>
        )}

        <div className="flex items-center gap-3 4xl:gap-10">
          {tools.map((tool, index) => (
            <React.Fragment key={tool.id}>
              {index > 0 && (
                <div className="w-px h-8 bg-[#DBE2EA]" />
              )}
              <button
                onClick={() => handleToolClick(tool.id)}
                onMouseEnter={(e) => {
                  setHoveredTool(tool.name);
                  if (isFullscreen) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltipPos({ top: rect.top, left: rect.left + rect.width / 2 });
                  }
                }}
                onMouseLeave={() => { setHoveredTool(null); setTooltipPos(null); }}
                className="
                  flex items-center justify-center w-8 h-8 xl:w-10 xl:h-10
                  2xl:w-12 2xl:h-12 rounded-[8px]
                  transition-all duration-300
                  hover:bg-[#EFF0F5]
                  hover:-translate-y-2
                  hover:scale-110
                  hover:shadow-lg
                "
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                {tool.icon}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>

      {isFullscreen && hoveredTool && tooltipPos && createPortal(
        <div
          className="bg-gray-800 text-white text-xs font-medium px-2 py-1 rounded-[4px] whitespace-nowrap shadow-lg pointer-events-none"
          style={{ position: "fixed", top: tooltipPos.top, left: tooltipPos.left, transform: "translate(-50%, calc(-100% - 6px))", zIndex: 99999, fontFamily: DEFAULT_FONT_FAMILY }}
        >
          {hoveredTool}
        </div>,
        document.body
      )}
    </>
  );
};

export default ToolBox;
